/**
 * Lightweight SEO helper — imperatively updates <title>, <meta>, and OG tags
 * for the current route. No dependency on react-helmet.
 *
 * Suitable for client-rendered SEO: Googlebot executes JS and will pick up
 * the updated tags. Social-media scrapers that don't execute JS will see the
 * defaults in index.html instead.
 */

function ensureMeta(name, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  return el;
}

function setLinkCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Apply a set of SEO tags to <head>. Pass only the keys you want to set.
 *
 * @param {object} opts
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.ogTitle]
 * @param {string} [opts.ogDescription]
 * @param {string} [opts.ogImage]
 * @param {string} [opts.ogType]
 * @param {string} [opts.canonical]
 * @param {string} [opts.robots]
 */
export function setSeo(opts) {
  if (!opts) return;
  if (opts.title) document.title = opts.title;
  if (opts.description) ensureMeta('description').setAttribute('content', opts.description);
  if (opts.robots) ensureMeta('robots').setAttribute('content', opts.robots);
  if (opts.ogTitle) ensureMeta('og:title', 'property').setAttribute('content', opts.ogTitle);
  if (opts.ogDescription) ensureMeta('og:description', 'property').setAttribute('content', opts.ogDescription);
  if (opts.ogImage) ensureMeta('og:image', 'property').setAttribute('content', opts.ogImage);
  if (opts.ogType) ensureMeta('og:type', 'property').setAttribute('content', opts.ogType);
  if (opts.ogTitle || opts.ogDescription) {
    // Twitter card fallback
    ensureMeta('twitter:card').setAttribute('content', 'summary_large_image');
    if (opts.ogTitle) ensureMeta('twitter:title').setAttribute('content', opts.ogTitle);
    if (opts.ogDescription) ensureMeta('twitter:description').setAttribute('content', opts.ogDescription);
    if (opts.ogImage) ensureMeta('twitter:image').setAttribute('content', opts.ogImage);
  }
  if (opts.canonical) setLinkCanonical(opts.canonical);
}
